import { Button, Dialog } from '@atoms'
import useMutation from '@hooks/useGQLMutation'
import { useModalReload } from '@hooks/useModalReload'
import { ArchiveCallingSessionDocument } from '@frontend/graphql/mutations/archiveCallingSession.generated'
import { scopedTranslation } from '@frontend/utils/I18n'
import { toast } from '@stores/toastStore'

const t = scopedTranslation('calling_sessions_index')
const tButtons = scopedTranslation('shared.buttons')

export type ArchiveCallingSessionDialogState = {
  type: 'archiveCallingSession'
  dialogData: {
    callingSessionId: string
    sessionName: string
  }
}

type ArchiveCallingSessionDialogProps = {
  onClose: () => void
  dialogState: ArchiveCallingSessionDialogState
}

export function ArchiveCallingSessionDialog({ onClose, dialogState }: ArchiveCallingSessionDialogProps) {
  const { callingSessionId, sessionName } = dialogState.dialogData
  const reload = useModalReload()
  const { mutateAsync, isLoading } = useMutation(ArchiveCallingSessionDocument)

  const handleArchive = async () => {
    try {
      const response = await mutateAsync({ callingSessionId })

      if (response?.archiveCallingSession?.success) {
        toast.success(t('alerts.archive_success'))
        reload({ only: ['callingSessions'] })
        onClose()
        return
      }

      toast.error(t('alerts.archive_error'))
    } catch (error) {
      console.error('Archive calling session mutation failed:', error)
      toast.error(t('alerts.archive_error'))
    }
  }

  return (
    <Dialog.Root open onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title className="tw:pb-2">{t('archive_dialog.title', { name: sessionName })}</Dialog.Title>
          <Dialog.Description>{t('archive_dialog.description')}</Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Dialog.Close asChild={true}>
            <Button type="button" size="xl" rank="secondary">
              {tButtons('cancel')}
            </Button>
          </Dialog.Close>
          <Button type="button" size="xl" color="danger" leadingIcon="archive" onClick={handleArchive} disabled={isLoading}>
            {t('archive_dialog.confirm')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
